import type { AggregatesArtifact, JapanMapArtifact, KansaiMapArtifact, MethodologyArtifact, ResponsesArtifact, Voice } from './types';
import {
  assertAggregatesArtifact,
  assertKansaiMapArtifact,
  assertMethodologyArtifact,
  assertJapanMapArtifact,
  assertResponsesArtifact,
  assertVoicesArtifact,
} from './validation';

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface DataClient {
  loadAggregates(): Promise<AggregatesArtifact>;
  preloadResponses(): Promise<ResponsesArtifact>;
  loadResponses(): Promise<ResponsesArtifact>;
  loadVoices(): Promise<Voice[]>;
  loadKansaiMap(): Promise<KansaiMapArtifact>;
  loadJapanMap(): Promise<JapanMapArtifact>;
  loadMethodology(): Promise<MethodologyArtifact>;
  retry(): void;
}

function createCachedLoader<T>(
  fetcher: FetchLike,
  url: string,
  assertArtifact: (value: unknown) => asserts value is T,
) {
  let pending: Promise<T> | undefined;
  let forceFresh = false;
  return {
    load(): Promise<T> {
      if (pending) return pending;
      const request = fetcher(url, {
        headers: { Accept: 'application/json' },
        cache: forceFresh ? 'no-store' : 'default',
      })
        .then(async (response) => {
          if (!response.ok) throw new Error(`${url} の読み込みに失敗しました (HTTP ${response.status})`);
          const value: unknown = await response.json();
          assertArtifact(value);
          return value;
        })
        .catch((error: unknown) => {
          if (pending === request) {
            pending = undefined;
            forceFresh = true;
          }
          throw error;
        });
      forceFresh = false;
      pending = request;
      return request;
    },
    clear() {
      pending = undefined;
      forceFresh = true;
    },
  };
}

export function createDataClient(fetcher: FetchLike = fetch): DataClient {
  const aggregates = createCachedLoader(fetcher, '/data/aggregates.json', assertAggregatesArtifact);
  const responses = createCachedLoader(fetcher, '/data/responses.json', assertResponsesArtifact);
  const voices = createCachedLoader(fetcher, '/data/voices.json', assertVoicesArtifact);
  const kansaiMap = createCachedLoader(
    fetcher,
    '/maps/kansai-municipalities.geojson',
    assertKansaiMapArtifact,
  );
  const japanMap = createCachedLoader(fetcher, '/maps/japan-prefectures.geojson', assertJapanMapArtifact);
  const methodology = createCachedLoader(fetcher, '/data/methodology.json', assertMethodologyArtifact);

  return {
    loadAggregates: () => aggregates.load(),
    preloadResponses: () => responses.load(),
    loadResponses: () => responses.load(),
    loadVoices: () => voices.load(),
    loadKansaiMap: () => kansaiMap.load(),
    loadJapanMap: () => japanMap.load(),
    loadMethodology: () => methodology.load(),
    retry() {
      aggregates.clear();
      responses.clear();
      voices.clear();
      kansaiMap.clear();
      japanMap.clear();
      methodology.clear();
    },
  };
}

export const dataClient = createDataClient();
